import React, { useCallback, useEffect, useRef, useState } from "react";
import { format, isValid } from "date-fns";
import { Check, Copy } from "lucide-react";
import type { CortexEventLogRow } from "@flowstate/types";
import { cn } from "../lib/cn";
import { copyEventToClipboard } from "../lib/copy-event";

interface EventDetailProps {
  event: CortexEventLogRow;
  devMode?: boolean;
  className?: string;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-2 text-xs py-1 border-b border-border/40">
      <span className="text-muted-foreground font-medium truncate">{label}</span>
      <span className="font-mono text-foreground break-all">{value}</span>
    </div>
  );
}

export function EventDetail({ event, devMode, className }: EventDetailProps) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const ok = await copyEventToClipboard(event);
    if (!ok) return;
    setCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
  }, [event]);

  return (
    <div className={cn("p-3 space-y-1 text-xs", className)}>
      <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-border/40">
        <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
          Event detail
        </span>
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? "Copied" : "Copy event JSON"}
          aria-label={copied ? "Copied event JSON" : "Copy event JSON"}
          className={cn(
            "h-7 px-2 min-h-[28px] rounded flex items-center gap-1.5",
            "text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-[10px]",
            copied && "text-green-400",
          )}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
          {copied ? "Copied" : "Copy JSON"}
        </button>
      </div>
      <Field label="ID" value={event.event_id ?? event.id} />
      <Field label="domain" value={event.domain} />
      <Field label="event_type" value={event.event_type} />
      <Field label="status" value={event.status ?? "—"} />
      <Field label="source" value={event.source ?? "—"} />
      <Field label="priority" value={event.priority ?? "—"} />
      {event.duration_ms != null && (
        <Field label="duration_ms" value={`${event.duration_ms}ms`} />
      )}
      {event.cortex_session_id && (
        <Field label="session_id" value={event.cortex_session_id} />
      )}
      {event.cortex_run_id && (
        <Field label="run_id" value={event.cortex_run_id} />
      )}
      {event.correlation_id && (
        <Field label="correlation_id" value={event.correlation_id} />
      )}
      {event.occurred_at && (() => {
        const date = new Date(event.occurred_at);
        return isValid(date) ? (
          <Field
            label="occurred_at"
            value={format(date, "HH:mm:ss.SSS")}
          />
        ) : null;
      })()}

      {devMode && event.payload && (
        <div className="mt-2 pt-2 border-t border-border/40">
          <p className="text-[10px] text-muted-foreground mb-1 font-mono uppercase tracking-wider">
            payload
          </p>
          <pre className="rounded bg-muted p-2 overflow-auto text-[10px] font-mono text-foreground max-h-48 sexy-scrollbar">
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </div>
      )}

      {devMode && event.metadata && (
        <div className="mt-2 pt-2 border-t border-border/40">
          <p className="text-[10px] text-muted-foreground mb-1 font-mono uppercase tracking-wider">
            metadata
          </p>
          <pre className="rounded bg-muted p-2 overflow-auto text-[10px] font-mono text-foreground max-h-48 sexy-scrollbar">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
